import React from 'react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { EventsListUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsListUnified';
import {
    FiltersState,
    FiltersStateStrategyType,
    FilterType,
} from 'src/domains/layouts/webview/components/filters/Filters.state';
import { computed, makeObservable } from 'mobx';
import { observer } from 'src/utils/mobx-react';
import { Filters } from 'src/domains/layouts/webview/components/filters/Filters';
import {
    EventsTableInfo,
    FootballFiltersWrapper,
} from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsTableMobX.style';
import { FiltersScrollableWrapper } from 'src/domains/layouts/webview/components/filters/FiltersScrollableWrapper';
import { PriceChangeLegend } from 'src/domains/layouts/webview/components/priceChangeLegend/PriceChangeLegend';
import { CouponIdType } from 'src/domains/sportsbook/state/eventsListState/coupons';
import { MatchType } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/markets';
import { UnifiedSportEventsListState } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedSportEventsListState';
import { EventsTableMarketsLoader } from 'src/domains/sportsbook/webview/components/loaders/EventsTableMarketsLoader';
import { useAppStateContext } from 'src/appState/AppState';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { FiltersWrapper } from 'src/domains/layouts/webview/components/sportHeader/SportHeaderFilters.style';

interface StatusPropsType {
    eventsListState: UnifiedSportEventsListState;
}

const Status = observer('StatusUnified', (props: StatusPropsType) => {
    const { showEvents, isLoading } = props.eventsListState;

    if (isLoading) {
        return <EventsTableMarketsLoader />;
    }

    if (showEvents === false) {
        return (
            <EventsTableInfo>
                <I18n
                    langKey='events.table.empty'
                    defaultText="Sorry, we haven't found any events with such criteria."
                />
            </EventsTableInfo>
        );
    }

    return null;
});

class UnifiedSportCouponFilterStrategy implements FiltersStateStrategyType {
    private static readonly COUPON_IDS: readonly CouponIdType[] = [
        'all',
        'today',
        'tomorrow',
        'custom-outrights-market',
    ];

    public constructor(private readonly eventsListState: UnifiedSportEventsListState) {
        makeObservable(this);
    }

    @computed public get filters(): FilterType[] {
        const translated = this.eventsListState.translatedCouponsForEnv;
        const byId = new Map(translated.map((c) => [c.id, c]));

        const out: FilterType[] = [];
        for (const id of UnifiedSportCouponFilterStrategy.COUPON_IDS) {
            const item = byId.get(id);
            if (item !== undefined) {
                out.push({
                    id: item.id,
                    key: item.id,
                    label: item.name,
                });
            }
        }

        return out;
    }

    public getActiveFilterId(): string {
        return this.eventsListState.filter_coupon;
    }

    public setActiveFilterId(id: string | number | null): void {
        if (typeof id === 'string') {
            this.eventsListState.changeFilter_coupon(id);
        }
    }
}

class EventsFiltersUnifiedStrategy implements FiltersStateStrategyType {
    public constructor(private readonly eventsListState: UnifiedSportEventsListState) {
        makeObservable(this);
    }

    @computed public get filters(): FilterType[] {
        return this.eventsListState.marketForEnv.map((item) => ({
            id: item.id,
            key: item.id,
            label: item.name,
        }));
    }

    public getActiveFilterId(): MatchType | null {
        return this.eventsListState.filter_market ?? null;
    }

    public setActiveFilterId(id: string | number | null): void {
        if (id === null || id === '') {
            return;
        }
        const strId = String(id);
        const item = this.eventsListState.marketForEnv.find((m) => m.id === strId);
        if (item !== undefined) {
            this.eventsListState.changeFilter_market(item.id);
        }
    }

    public isFilterDisabled(): boolean {
        return this.eventsListState.isOutrightsMarket();
    }
}

class UnifiedSportCompetitionFilterStrategy implements FiltersStateStrategyType<CompetitionId | null> {
    public constructor(
        private readonly sportWideState: UnifiedSportEventsListState,
        private readonly getIds: {
            getCompetitionId: (numericId: number) => CompetitionId;
        },
        private readonly getSelectedCompetitionId: () => CompetitionId | null,
        private readonly setSelectedCompetitionId: (id: CompetitionId | null) => void,
        private readonly allCompetitionsLabel: JSX.Element
    ) {
        makeObservable(this);
    }

    @computed public get filters(): FilterType<CompetitionId | null>[] {
        const rows = this.sportWideState.competitionsArray.map((c) => ({
            id: this.getIds.getCompetitionId(c.id),
            key: String(c.id),
            label: c.name,
        }));

        return [{ id: null, key: 'all', label: this.allCompetitionsLabel }, ...rows];
    }

    public getActiveFilterId(): CompetitionId | null {
        return this.getSelectedCompetitionId();
    }

    public setActiveFilterId(id: CompetitionId | null): void {
        this.setSelectedCompetitionId(id);
    }
}

interface EventsTableUnifiedPropsType {
    competitionId: CompetitionId | null;
    sportId: string;
}

export const EventsTableUnified = observer('EventsTableUnified', (props: EventsTableUnifiedPropsType) => {
    const { appSportsBookState } = useAppStateContext();
    const common = useCommon();
    const { competitionId } = props;
    const sportIdKey = props.sportId;
    const configState = ConfigComponents.get(common).config;

    const [sportPageCompetitionFilter, setSportPageCompetitionFilter] = React.useState<CompetitionId | null>(null);

    const sportWideListState = appSportsBookState.unifiedSportEventsListState.get(sportIdKey).get(null);

    const effectiveCompetitionId: CompetitionId | null =
        competitionId !== null ? competitionId : sportPageCompetitionFilter;

    const eventsListState = appSportsBookState.unifiedSportEventsListState.get(sportIdKey).get(effectiveCompetitionId);

    React.useEffect(() => {
        setSportPageCompetitionFilter(null);
    }, [sportIdKey]);

    React.useEffect(() => {
        if (competitionId !== null || sportPageCompetitionFilter === null) {
            return;
        }
        const validIds = new Set(sportWideListState.competitionsArray.map((c) => c.id));
        if (!validIds.has(sportPageCompetitionFilter.toOldId())) {
            setSportPageCompetitionFilter(null);
        }
    }, [competitionId, sportPageCompetitionFilter, sportWideListState.competitionsArray]);

    const couponFiltersState = new FiltersState(
        new UnifiedSportCouponFilterStrategy(eventsListState),
        'unified-events-coupons'
    );
    const marketFiltersState = new FiltersState(new EventsFiltersUnifiedStrategy(eventsListState));

    const showSportCompetitionRow = competitionId === null && sportWideListState.competitionsArray.length > 1;

    const allCompetitionsLabel = (
        <I18n
            langKey='events.table.filter.all-competitions'
            defaultText='All competitions'
        />
    );

    const competitionFiltersState =
        showSportCompetitionRow === true
            ? new FiltersState(
                  new UnifiedSportCompetitionFilterStrategy(
                      sportWideListState,
                      appSportsBookState.models.id,
                      () => sportPageCompetitionFilter,
                      setSportPageCompetitionFilter,
                      allCompetitionsLabel
                  ),
                  'unified-competitions'
              )
            : null;

    return (
        <div
            className='events-table--sport-list experiments-unified-events-list'
            data-testid='events-table-unified'
        >
            <FiltersWrapper
                variant={configState.componentVariants.sportsbookVariant}
                data-testid='events-table-unified-coupon-filters'
            >
                <FiltersScrollableWrapper>
                    <Filters
                        state={couponFiltersState}
                        skin={configState.filtersVersion.football}
                    />
                </FiltersScrollableWrapper>
            </FiltersWrapper>

            {competitionFiltersState !== null ? (
                <FiltersWrapper
                    variant={configState.componentVariants.sportsbookVariant}
                    data-testid='events-table-unified-competition-filters'
                >
                    <FiltersScrollableWrapper>
                        <Filters
                            state={competitionFiltersState as FiltersState}
                            skin={configState.filtersVersion.football}
                        />
                    </FiltersScrollableWrapper>
                </FiltersWrapper>
            ) : null}

            {marketFiltersState.filters.length > 0 ? (
                <FootballFiltersWrapper
                    renderEventFilters={effectiveCompetitionId === null}
                    variant={configState.componentVariants.sportsbookVariant}
                >
                    <FiltersScrollableWrapper>
                        <Filters
                            state={marketFiltersState}
                            skin={configState.filtersVersion.football}
                        />
                    </FiltersScrollableWrapper>
                </FootballFiltersWrapper>
            ) : null}

            {!eventsListState.isLoading && (
                <EventsListUnified
                    eventsListState={eventsListState}
                    sportId={sportIdKey}
                />
            )}
            <Status eventsListState={eventsListState} />

            {eventsListState.showEvents ? <PriceChangeLegend isRace={false} /> : null}
        </div>
    );
});
